// One session's card in a project's session list (Brief 4 / P8). The whole card
// is the drop-in: one click enters that session's OPERATORS (the operator-level
// chat), where you read + steer it. Presentational + pure: the tone (and whether
// the signal pulses) is decided by `cellTone`; `nowMs` is injected so the
// relative time is deterministic and the card never runs its own clock. A
// needs_input card renders its reason in WORDS and carries the attention signal.

import { Link } from "react-router-dom";
import type { SessionPresence } from "@waytide/core";
import {
  cellTone,
  reasonLabel,
  relativeActivity,
  statusLabel,
} from "@waytide/ui";
import { sessionLabel } from "@waytide/core";

export function SessionCell({
  session,
  nowMs,
  static: isStatic = false,
}: {
  session: SessionPresence;
  nowMs: number;
  /** Render as a non-interactive <div> instead of a navigating <Link> — for a
   * presentational context with no router (e.g. the public landing's glance),
   * where the cell is a demonstration, not a destination. Default false (the
   * dashboard's real, clickable cell). */
  static?: boolean;
}) {
  const tone = cellTone(session);
  const reason = reasonLabel(session.needs_input_reason);
  const status = statusLabel(session.status);
  const inner = (
    <>
      <div className="wt-session-cell__head">
        {/* The shared session identity: this cell MORPHS into the session
            header on navigation (the "card you tap becomes the
            session"). The matching name lives on SessionView's title. */}
        <span
          className="wt-session-cell__id"
          style={{ viewTransitionName: `session-${session.thread_id}` }}
          title={`Session #${session.thread_id}`}
        >
          {sessionLabel(session)}
        </span>
        <span className="wt-session-cell__status">
          <span className="wt-session-signal" aria-hidden="true" />
          {status}
        </span>
      </div>
      {reason ? <div className="wt-session-cell__reason">{reason}</div> : null}
      <div className="wt-session-cell__activity">
        Last active {relativeActivity(session.last_activity_at, nowMs)}
      </div>
      <span className="wt-session-cell__cue" aria-hidden="true">
        Open operators ›
      </span>
    </>
  );
  if (isStatic) {
    return (
      <div
        className={`wt-session-cell wt-session-cell--${tone}`}
        data-tone={tone}
        data-thread-id={session.thread_id}
        aria-label={`${sessionLabel(session)} — session ${
          session.thread_id
        } — ${status}${reason ? `, ${reason}` : ""}.`}
      >
        {inner}
      </div>
    );
  }
  return (
    <Link
      className={`wt-session-cell wt-session-cell--${tone}`}
      to={`/dashboard/s/${session.thread_id}`}
      viewTransition
      data-tone={tone}
      data-thread-id={session.thread_id}
      aria-label={`${sessionLabel(session)} — session ${
        session.thread_id
      } — ${status}${reason ? `, ${reason}` : ""}. Open the operators.`}
    >
      {inner}
    </Link>
  );
}
